import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import ApsReviewModal from "./ApsReviewModal";

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (value === "complete" || value === "high") {
    return "bg-emerald-100 text-emerald-800";
  }
  if (value === "incomplete" || value === "low" || value === "medium") {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-rose-100 text-rose-800";
}

function formatWhen(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return "";
  }
}

const ListingDocumentSummarySection = () => {
  const { isAdmin, isSuperUser, isBroker } = useRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openListing, setOpenListing] = useState(null);
  const [generatingId, setGeneratingId] = useState("");
  const knownEmailedRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get("/listing-documents/document-reviews");
      const next = res.data.reviews || [];
      if (knownEmailedRef.current) {
        const known = knownEmailedRef.current;
        next.forEach((row) => {
          const key = `${row.listingId}:${row.agentEmailedAt || ""}`;
          if (
            row.emailedToAgent &&
            row.agentEmailedAt &&
            !known.has(key)
          ) {
            const emails = (row.agentEmailedTo || []).filter(Boolean);
            toast.success(
              emails.length
                ? `Compliance review was sent to ${emails.join(", ")}.`
                : "Compliance review was sent to the agent.",
              { autoClose: 10000 },
            );
          }
        });
      }
      knownEmailedRef.current = new Set(
        next
          .filter((row) => row.emailedToAgent && row.agentEmailedAt)
          .map((row) => `${row.listingId}:${row.agentEmailedAt}`),
      );
      setRows(next);
    } catch (_) {
      if (!silent) setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), 10000);
    return () => window.clearInterval(timer);
  }, []);

  if (!isAdmin && !isSuperUser && !isBroker) {
    return (
      <p className="text-gray-500">
        Document summaries are available after a Super User reviews the listing.
      </p>
    );
  }

  const openReport = (row) => {
    setOpenListing({
      listingId: row.listingId,
      listingNumber: row.listingNumber,
    });
  };

  const generate = async (row) => {
    setGeneratingId(row.listingId);
    try {
      await axiosInstance.post(
        `/listing-documents/${row.listingId}/document-review`,
        {},
        { timeout: 180000 },
      );
      await load();
      openReport(row);
    } catch (_) {
      await load();
    } finally {
      setGeneratingId("");
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Compliance Review</h2>
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-gray-500">
          No listings are waiting for compliance review yet.
        </p>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const hasReport = Boolean(row.hasReport);
            const queued = Boolean(row.queuedAt) && !hasReport;
            const emailed = Boolean(row.emailedToAgent);
            return (
              <div
                key={row.listingId}
                className="bg-white rounded-lg shadow p-5 border border-gray-100"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Listing #{row.listingNumber}
                    </h3>
                    <p className="text-gray-600">{row.address || "No address"}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {emailed ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        Sent to agent
                      </span>
                    ) : null}
                    {row.overallStatus ? (
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusTone(
                          row.overallStatus,
                        )}`}
                      >
                        {row.overallStatus.replace(/_/g, " ")}
                      </span>
                    ) : queued ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Generating…
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        No report yet
                      </span>
                    )}
                  </div>
                </div>
                {hasReport && row.summary ? (
                  <p className="text-sm text-gray-800 leading-relaxed mb-3">
                    {row.summary}
                  </p>
                ) : queued ? (
                  <p className="text-sm text-gray-500 mb-3">
                    Automated compliance review is running. The agent will be
                    emailed when it finishes, and this card will show the sent
                    report.
                  </p>
                ) : row.hasFiles ? (
                  <p className="text-sm text-gray-500 mb-3">
                    Documents are uploaded, but the compliance review has not
                    been generated yet.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mb-3">
                    Listing saved. Upload the listing agreement under Documents,
                    then generate the report.
                  </p>
                )}
                {emailed && row.agentEmailedAt ? (
                  <p className="text-xs text-blue-800 mb-3">
                    Review was sent to{" "}
                    {(row.agentEmailedTo || []).filter(Boolean).join(", ") ||
                      "the agent"}{" "}
                    {formatWhen(row.agentEmailedAt)}.
                  </p>
                ) : null}
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-400"
                  disabled={
                    generatingId === row.listingId ||
                    queued ||
                    (!hasReport && !row.hasFiles)
                  }
                  onClick={() =>
                    hasReport ? openReport(row) : generate(row)
                  }
                >
                  {generatingId === row.listingId || queued
                    ? "Preparing..."
                    : hasReport
                      ? "Open report"
                      : "Generate report"}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {openListing ? (
        <ApsReviewModal
          listingId={openListing.listingId}
          listingNumber={openListing.listingNumber}
          onClose={() => setOpenListing(null)}
        />
      ) : null}
    </div>
  );
};

export default ListingDocumentSummarySection;
